///
/// EarthBrowser
///
/// Created by Matt Giger on 9/13/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Validation for the persisted engagement slice. Malformed fields fall
/// back to their initial values rather than failing the whole slice: this
/// state is best-effort funnel bookkeeping, and "looks like a new visitor"
/// is always a safe repair.
///

import type { EngagementState } from "../engagementSlice"

const cDayPattern = /^\d{4}-\d{2}-\d{2}$/

function isISODateString(value: unknown): value is string {
	return typeof value === "string" && !isNaN(Date.parse(value))
}

/**
 * Validate the whole slice, repairing malformed fields field-by-field.
 */
export function validateEngagementState(state: unknown): EngagementState {
	const fallback: EngagementState = {
		anonymousId: null,
		firstSeenAt: null,
		sessionCount: 0,
		lastSessionDay: null,
		firstSessionWasDeepLink: false,
		lastSessionWasDeepLink: false,
		interstitialLastShownDay: null,
		interstitialShowCount: 0,
		interstitialPendingDelay: null,
		bannerDismissedDay: null,
		savedViewPromptShown: false,
		offerSnoozedUntil: null,
	}
	if (!state || typeof state !== "object") return fallback
	const s = state as Partial<EngagementState>

	const validCount = (value: unknown): number =>
		typeof value === "number" && isFinite(value) && value >= 0 ? Math.floor(value) : 0

	const validDay = (value: unknown): string | null =>
		typeof value === "string" && cDayPattern.test(value) ? value : null

	return {
		anonymousId: typeof s.anonymousId === "string" && s.anonymousId.length > 0 ? s.anonymousId : null,
		firstSeenAt: isISODateString(s.firstSeenAt) ? s.firstSeenAt : null,
		sessionCount: validCount(s.sessionCount),
		lastSessionDay: validDay(s.lastSessionDay),
		firstSessionWasDeepLink: s.firstSessionWasDeepLink === true,
		lastSessionWasDeepLink: s.lastSessionWasDeepLink === true,
		interstitialLastShownDay: validDay(s.interstitialLastShownDay),
		interstitialShowCount: validCount(s.interstitialShowCount),
		interstitialPendingDelay: typeof s.interstitialPendingDelay === "number" &&
			isFinite(s.interstitialPendingDelay) && s.interstitialPendingDelay >= 0
			? Math.floor(s.interstitialPendingDelay)
			: null,
		bannerDismissedDay: validDay(s.bannerDismissedDay),
		savedViewPromptShown: s.savedViewPromptShown === true,
		offerSnoozedUntil: isISODateString(s.offerSnoozedUntil) ? s.offerSnoozedUntil : null,
	}
}
